'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/features/authentication/presentation/context/AuthContext';
import { useUser } from '@/features/app/user/presentation/context/UserContext';
import {
  PRODUCT_DEMO_PREVIEW_QUERY_KEY,
  PRODUCT_DEMO_PREVIEW_VALUE_REMINDER_SETTINGS,
  PRODUCT_DEMO_SESSION_SPOTLIGHT_KEY,
  PRODUCT_DEMO_SESSION_WELCOME_KEY,
  PRODUCT_DEMO_SESSION_WHATSAPP_SPOTLIGHT_KEY,
} from '../constants/product-demo-reminder-settings.constants';

export type ReminderSettingsDemoPhase = 'welcome' | 'whatsapp_spotlight' | 'schedule_spotlight' | 'idle';

function readSessionFlag(key: string): boolean {
  try {
    return sessionStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

function writeSessionFlag(key: string): void {
  try {
    sessionStorage.setItem(key, '1');
  } catch {
    /* storage indisponível (modo privado, etc.) */
  }
}

export interface UseReminderSettingsProductDemoResult {
  /** Mostrar faixa “Demonstração” e camadas de tour. */
  demoUiActive: boolean;
  /** Preview via query string (DEV): não grava `skipped` no Firestore. */
  previewMode: boolean;
  phase: ReminderSettingsDemoPhase;
  completeWelcome: () => void;
  completeWhatsappSpotlight: () => Promise<void>;
  completeSpotlight: () => void;
  skipDemo: () => Promise<void>;
}

export function useReminderSettingsProductDemo(): UseReminderSettingsProductDemoResult {
  const { state: authState } = useAuth();
  const { state: userState, events: userEvents } = useUser();
  const [searchParams] = useSearchParams();
  const [previewDismissed, setPreviewDismissed] = useState(false);
  const [phase, setPhase] = useState<ReminderSettingsDemoPhase>('idle');

  const user = userState.currentUser;

  const fromProfile = Boolean(
    (user?.product_demo_last_step === 'whatsapp_test' || user?.product_demo_last_step === 'reminder_settings') &&
      user.product_demo_skipped_at == null &&
      user.product_demo_completed_at == null
  );

  const fromPreview = Boolean(
    import.meta.env.DEV &&
      searchParams.get(PRODUCT_DEMO_PREVIEW_QUERY_KEY) === PRODUCT_DEMO_PREVIEW_VALUE_REMINDER_SETTINGS
  );

  const isDemoGateOpen = fromProfile || fromPreview;
  const previewMode = fromPreview && !fromProfile;

  const demoUiActive = isDemoGateOpen && !(previewMode && previewDismissed);

  /** No preview, o fluxo começa como se o passo atual fosse `whatsapp_test`. */
  const effectiveDemoLastStep = previewMode ? 'whatsapp_test' : user?.product_demo_last_step;

  const resolvePhaseAfterWelcome = useCallback((): ReminderSettingsDemoPhase => {
    if (effectiveDemoLastStep === 'whatsapp_test' && !readSessionFlag(PRODUCT_DEMO_SESSION_WHATSAPP_SPOTLIGHT_KEY)) {
      return 'whatsapp_spotlight';
    }
    if (!readSessionFlag(PRODUCT_DEMO_SESSION_SPOTLIGHT_KEY)) {
      return 'schedule_spotlight';
    }
    return 'idle';
  }, [effectiveDemoLastStep]);

  useEffect(() => {
    if (!isDemoGateOpen || (previewMode && previewDismissed)) {
      setPhase('idle');
      return;
    }
    if (!readSessionFlag(PRODUCT_DEMO_SESSION_WELCOME_KEY)) {
      setPhase('welcome');
      return;
    }
    setPhase(resolvePhaseAfterWelcome());
  }, [isDemoGateOpen, previewMode, previewDismissed, resolvePhaseAfterWelcome, user?.product_demo_last_step]);

  const completeWelcome = useCallback(() => {
    writeSessionFlag(PRODUCT_DEMO_SESSION_WELCOME_KEY);
    setPhase(resolvePhaseAfterWelcome());
  }, [resolvePhaseAfterWelcome]);

  const completeWhatsappSpotlight = useCallback(async (): Promise<void> => {
    writeSessionFlag(PRODUCT_DEMO_SESSION_WHATSAPP_SPOTLIGHT_KEY);
    if (!previewMode) {
      const uid = authState.user?.uid;
      if (uid) {
        await userEvents.updateUser(uid, { product_demo_last_step: 'reminder_settings' });
      }
    }
    if (!readSessionFlag(PRODUCT_DEMO_SESSION_SPOTLIGHT_KEY)) {
      setPhase('schedule_spotlight');
    } else {
      setPhase('idle');
    }
  }, [authState.user?.uid, previewMode, userEvents]);

  const completeSpotlight = useCallback(() => {
    writeSessionFlag(PRODUCT_DEMO_SESSION_SPOTLIGHT_KEY);
    setPhase('idle');
  }, []);

  const skipDemo = useCallback(async (): Promise<void> => {
    if (previewMode) {
      setPreviewDismissed(true);
      setPhase('idle');
      return;
    }
    const uid = authState.user?.uid;
    if (uid) {
      const skippedAt = new Date().toISOString();
      await userEvents.updateUser(uid, { product_demo_skipped_at: skippedAt });
    }
    setPhase('idle');
  }, [authState.user?.uid, previewMode, userEvents]);

  return useMemo(
    () => ({
      demoUiActive,
      previewMode,
      phase,
      completeWelcome,
      completeWhatsappSpotlight,
      completeSpotlight,
      skipDemo,
    }),
    [demoUiActive, previewMode, phase, completeWelcome, completeWhatsappSpotlight, completeSpotlight, skipDemo]
  );
}
